import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  CreditCard,
  ArrowLeft,
  Mail,
  Phone,
  User,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { bookingsAPI, locationsAPI } from '../utils/api';
import { toast } from 'sonner';

const STATUS_META = {
  pending: { label: 'Čekající platba', cls: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
  pending_approval: { label: 'Čeká na schválení hostitelem', cls: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertCircle },
  approved: { label: 'Schváleno – zaplaťte', cls: 'bg-blue-100 text-blue-700 border-blue-300', icon: CheckCircle2 },
  rejected: { label: 'Odmítnuto hostitelem', cls: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
  confirmed: { label: 'Potvrzená rezervace', cls: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 },
  cancelled: { label: 'Zrušeno', cls: 'bg-gray-100 text-gray-700 border-gray-300', icon: XCircle },
};

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('currentUser');
    if (!stored) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const bookingRes = await bookingsAPI.getById(id);
        setBooking(bookingRes.data);
        const locRes = await locationsAPI.getById(bookingRes.data.location_id);
        setLocation(locRes.data);
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.detail || 'Rezervace nenalezena');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-40 text-center text-gray-600">Načítám rezervaci…</div>
      </div>
    );
  }

  if (!booking || !location) return null;

  const meta = STATUS_META[booking.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const host = location.owner || {};

  const nights = (() => {
    const a = new Date(booking.check_in);
    const b = new Date(booking.check_out);
    return Math.max(1, Math.round((b - a) / (1000 * 60 * 60 * 24)));
  })();

  const mailHostHref = host.email
    ? `mailto:${host.email}?subject=${encodeURIComponent(
        `Dotaz k rezervaci #${booking.id.slice(0, 8)} – ${location.name}`
      )}&body=${encodeURIComponent(
        `Dobrý den,\n\nmám dotaz ohledně mé rezervace na lokalitě "${location.name}".\n\n` +
        `Rezervace: ${booking.id}\nPříjezd: ${booking.check_in}\nOdjezd: ${booking.check_out}\nHosté: ${booking.guests}\n\nDěkuji,\n`
      )}`
    : null;

  const telHostHref = host.phone ? `tel:${host.phone.replace(/\s+/g, '')}` : null;

  // Pay-now handler for guests with status=approved
  const handlePayNow = async () => {
    if (paying) return;
    setPaying(true);
    const loadingId = toast.loading('Vytváření platby…');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ booking_id: booking.id, origin_url: window.location.origin }),
      });
      // Read body ONCE as text, then try to parse JSON — avoids
      // "body stream already read" if anything else (e.g. browser dev-tools
      // or a service worker) had already touched the response.
      const raw = await response.text();
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }

      toast.dismiss(loadingId);

      if (!response.ok) {
        const msg = (data && data.detail) ? data.detail : `Chyba ${response.status}: ${raw.slice(0, 120) || 'nepodařilo se získat odpověď'}`;
        throw new Error(msg);
      }
      if (!data || !data.checkout_url) {
        throw new Error('Server nevrátil platební URL');
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.dismiss(loadingId);
      console.error('[handlePayNow] error:', e);
      toast.error(e.message || 'Chyba při vytváření platby');
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div
        className="relative py-16"
      >
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-2 text-white">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10 mb-3 -ml-3"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Zpět na dashboard
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Detail rezervace</h1>
          <p className="text-white/80 text-sm break-all">ID: {booking.id}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-lg" data-testid="booking-status-card">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Badge className={`${meta.cls} border px-3 py-1.5 text-sm font-semibold`}>
                    <StatusIcon className="w-4 h-4 mr-1.5 inline" />
                    {meta.label}
                  </Badge>
                  {booking.status === 'pending_approval' && booking.approval_deadline && (
                    <span className="text-xs text-gray-500">
                      Termín schválení: {new Date(booking.approval_deadline).toLocaleString('cs-CZ')}
                    </span>
                  )}
                </div>

                {/* PROMINENT pay-now CTA when host approved the booking */}
                {booking.status === 'approved' && (
                  <div className="mb-5 p-5 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 shadow-sm" data-testid="pay-now-cta">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-orange-900 mb-1">
                          Hostitel rezervaci schválil – zaplaťte
                        </h3>
                        <p className="text-sm text-orange-800">
                          Vaše rezervace bude potvrzená až po zaplacení.
                          Bezpečná platba kartou přes Stripe.
                        </p>
                        <p className="text-2xl font-bold text-orange-700 mt-2">
                          {booking.total_price} Kč
                        </p>
                      </div>
                      <Button
                        size="lg"
                        onClick={handlePayNow}
                        disabled={paying}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-bold shadow-lg whitespace-nowrap"
                        data-testid="pay-now-btn"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        {paying ? 'Načítám…' : 'Zaplatit nyní'}
                      </Button>
                    </div>
                  </div>
                )}

                <Link
                  to={`/location/${location.id}`}
                  className="block group"
                  data-testid="booking-location-link"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <img
                      src={location.photos?.[0] || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
                      alt={location.name}
                      className="w-full sm:w-40 h-32 sm:h-28 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h2 className="text-xl font-bold group-hover:text-orange-600 transition-colors">
                        {location.name}
                      </h2>
                      <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {location.type}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{location.description}</p>
                    </div>
                  </div>
                </Link>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" /> Detail pobytu
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Příjezd</p>
                    <p className="font-semibold" data-testid="booking-checkin">{booking.check_in}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Odjezd</p>
                    <p className="font-semibold" data-testid="booking-checkout">{booking.check_out}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Počet nocí</p>
                    <p className="font-semibold">{nights}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1 flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" /> Hosté
                    </p>
                    <p className="font-semibold">{booking.guests}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Cena za noc</p>
                    <p className="font-semibold">{Math.round(booking.total_price / nights)} Kč</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1 flex items-center gap-1">
                      <CreditCard className="w-3.5 h-3.5" /> Celkem
                    </p>
                    <p className="font-bold text-orange-600 text-lg">{booking.total_price} Kč</p>
                  </div>
                </div>

                {booking.refund_amount > 0 && (
                  <>
                    <Separator className="my-4" />
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm">
                      <p className="font-semibold text-yellow-800 mb-1">Vrácená částka</p>
                      <p className="text-yellow-700">
                        {booking.refund_amount} Kč
                        {booking.cancelled_at && (
                          <span className="text-xs text-yellow-600 ml-2">
                            (zrušeno {new Date(booking.cancelled_at).toLocaleDateString('cs-CZ')})
                          </span>
                        )}
                      </p>
                    </div>
                  </>
                )}

                {booking.rejection_reason && (
                  <>
                    <Separator className="my-4" />
                    <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
                      <p className="font-semibold text-red-800 mb-1">Důvod odmítnutí hostitelem</p>
                      <p className="text-red-700">{booking.rejection_reason}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Host */}
          <div className="space-y-6">
            <Card className="shadow-lg" data-testid="host-contact-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-orange-600" /> Hostitel
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                <div>
                  <p className="font-semibold text-base">{host.name || 'Hostitel'}</p>
                </div>

                {host.email && (
                  <div className="flex items-start gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <a
                      href={`mailto:${host.email}`}
                      className="text-gray-700 hover:text-orange-600 break-all"
                      data-testid="host-email"
                    >
                      {host.email}
                    </a>
                  </div>
                )}

                {host.phone && (
                  <div className="flex items-start gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <a
                      href={telHostHref}
                      className="text-gray-700 hover:text-orange-600"
                      data-testid="host-phone"
                    >
                      {host.phone}
                    </a>
                  </div>
                )}

                {mailHostHref && (
                  <Button
                    asChild
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="contact-host-btn"
                  >
                    <a href={mailHostHref}>
                      <Mail className="w-4 h-4 mr-2" />
                      Kontaktovat hostitele
                    </a>
                  </Button>
                )}

                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Kontaktujte hostitele pouze v případě dotazů k pobytu. WildCamping nezprostředkovává platby mimo platformu.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingDetail;
